import type { HTMLAttributes, PropsWithChildren } from 'react';
import { createContext, useContext, useState } from 'react';
import styled from 'styled-components';
import type { FontValue } from '@elfsight/configurator-contracts';
import { createPortal } from 'react-dom';
import { usePortalNestContainer } from './use-portal-nest-container';

const PortalNestContext = createContext<HTMLDivElement | null>(null);

export type PortalNestProviderProps = HTMLAttributes<HTMLDivElement> &
  PropsWithChildren<{
    customCSSClassName?: string;
    font?: FontValue;
  }>;

const ROOT_ID = '__EAAPS_PORTAL';
const STATIC_CLASS = 'es-portal-root';

export function PortalNestProvider({
  children,
  className,
  customCSSClassName,
  font,
  ...forwardedProps
}: PortalNestProviderProps) {
  const fullClassName = [STATIC_CLASS, customCSSClassName, className]
    .filter(Boolean)
    .join(' ');
  const [nestElement, setNestElement] = useState<HTMLDivElement | null>(null);
  const portalContainer = usePortalNestContainer();

  return (
    <PortalNestContext.Provider value={nestElement}>
      {nestElement && children}
      {createPortal(
        <ElfsightPortalNest
          _font={font}
          id={ROOT_ID}
          className={fullClassName}
          ref={setNestElement}
          {...forwardedProps}
        ></ElfsightPortalNest>,
        portalContainer
      )}
    </PortalNestContext.Provider>
  );
}

const ElfsightPortalNest = styled.div<{ _font?: FontValue }>`
  -webkit-font-smoothing: antialiased;
  ${({ _font }) => _font?.value && `font-family: "${_font.value}";`}
`;

export const usePortalNestProvider = () => useContext(PortalNestContext);
